import React, { useState } from "react";
import { useOutletContext } from "react-router-dom";
import useSWRMutation from "swr/mutation";

import MutableListView from "../../../components/MutableListView";
import DataFetcher from "../../../components/DataFetcher";
import { InputField } from "../../../components/Input";
import Modal from "../../../components/Modal";
import CheckBox from "../../../components/Checkbox";
import {
  resolveFileUrl,
  sendDeleteRequest,
  sendPatchRequest,
  sendPostRequest,
} from "../../../config/swr";
import useForm from "../../../hooks/useForm.hook";
import useMessageModal from "../../../hooks/useMessageModal.hook";

const PopupAdsPage = () => {
  const [setPageTitle] = useOutletContext();
  setPageTitle("Popup Adverts");

  return (
    <DataFetcher
      url="/ads"
      buildUI={(data, mutate) => (
        <PopupAdsDisplay data={data?.data ?? []} mutate={mutate} />
      )}
    />
  );
};

const PopupAdsDisplay = ({ data, mutate }) => {
  const [adData, setAdData] = useState(null);

  const { trigger: createTrigger } = useSWRMutation(
    "/ads/create",
    sendPostRequest
  );

  const { trigger: patchTrigger } = useSWRMutation(
    "/ads",
    sendPatchRequest
  );

  const { trigger: deleteTrigger } = useSWRMutation(
    "/ads/",
    sendDeleteRequest
  );

  const form = useForm();
  const showMessageModal = useMessageModal();

  const closeModal = () => {
    setAdData(null);
    form.submitSuccess();
  };

  const openModal = (_, item) => {
    setAdData({
      id: item?.id,
      title: item?.title ?? "",
      body: item?.body ?? "",
      imageUrl: resolveFileUrl(item?.imageUrl) ?? "",
      actionUrl: item?.actionUrl ?? "",
      isActive: item?.isActive ?? true,
      forMobile: item?.forMobile ?? false,
      image: null,
    });
  };

  const onChange = (e) => {
    const { name, value } = e.target;
    setAdData({ ...adData, [name]: value });
  };

  const onFileChange = (e) => {
    const file = e.target.files?.[0] ?? null;
    setAdData({
      ...adData,
      image: file,
      imageUrl: file ? URL.createObjectURL(file) : adData?.imageUrl,
    });
  };

  const onToggleForMobile = () => {
    setAdData({ ...adData, forMobile: !adData?.forMobile });
  };

  const submitForm = async (event) => {
    event.preventDefault();
    if (!adData?.title || !adData?.body) return;
    form.submitStarted();

    try {
      if (adData?.id) {
        const payload = {
          title: adData.title,
          body: adData.body,
          imageUrl: adData.imageUrl,
          actionUrl: adData.actionUrl,
          isActive: adData.isActive ?? false,
          forMobile: adData.forMobile ?? false,
        };
        await patchTrigger({ subUrl: `/${adData.id}`, payload });
      } else {
        const formData = new FormData(event.target);
        // Normalize booleans and enforce isActive=true for create
        formData.set("forMobile", adData.forMobile ? "true" : "false");
        formData.set("isActive", "true");
        if (adData.image) formData.set("image", adData.image);
        await createTrigger(formData);
      }
      form.submitSuccess();
      closeModal();
      mutate();
    } catch (error) {
      showMessageModal({
        title: "Error",
        message:
          error?.response?.data?.errorMessage ||
          "An error occurred. Please try again",
        isError: true,
        closeButtonText: "Close",
      });
      form.submitError();
    }
  };

  const deleteAd = async (index, onSuccess, onError) => {
    const { id } = data[index];
    try {
      const res = await deleteTrigger(id);
      onSuccess(res.data?.message || "Successfully removed");
      mutate();
    } catch (error) {
      showMessageModal({
        title: "Error",
        message:
          error?.response?.data?.errorMessage ||
          "An error occurred. Please try again",
        isError: true,
        closeButtonText: "Close",
      });
      onError();
    }
  };

  const toggleStatus = async (item) => {
    try {
      await patchTrigger({ subUrl: `/${item.id}/status`, payload: {} });
      mutate();
    } catch (error) {
      showMessageModal({
        title: "Error",
        message:
          error?.response?.data?.errorMessage ||
          "An error occurred. Please try again",
        isError: true,
        closeButtonText: "Close",
      });
    }
  };

  const addBtnDisabled = form.state.submitting; // no hard cap for popups

  return (
    <div className="ads">
      <div className="ads__heading flex between-center fs-20">
        <p>POPUP ADVERT SECTION</p>
        <span>Manage popup messages</span>
      </div>
      <div className="ads__table">
        <table>
          <tbody>
            <MutableListView
              dataList={data}
              renderItem={(item, editButton, deleteButton) => (
                <tr key={item.id} className="flex center">
                  <td>
                    <div className="ads__table-title fs-20">{item.title}</div>
                    <div className="ads__table-subtitle fs-16 charcoal-60">
                      {item.body}
                    </div>
                    <div className="fs-14 charcoal-60">Image: {item.imageUrl || "-"}</div>
                    <div className="fs-14 charcoal-60">Action: {item.actionUrl || "-"}</div>
                  </td>
                  <td>
                    <span className="fs-14" style={{ color: item.isActive ? "#009B54" : "#879297" }}>
                      {item.isActive ? "Active" : "Inactive"}
                    </span>
                  </td>
                  <td>
                    <span className="fs-14 charcoal-60">{item.forMobile ? "Mobile" : "General"}</span>
                  </td>
                  <td>
                    <button
                      className="btn primary light"
                      onClick={() => toggleStatus(item)}
                      disabled={form.state.submitting}
                    >
                      {item.isActive ? "Deactivate" : "Activate"}
                    </button>
                  </td>
                  <td>{editButton}</td>
                  <td>{deleteButton}</td>
                </tr>
              )}
              editItem={openModal}
              deleteItem={deleteAd}
            />

            <tr>
              <td colSpan={6}>
                <p className="ads__table-newad-title fs-18">
                  <button onClick={() => setAdData({ title: "", body: "", imageUrl: "", actionUrl: "", isActive: false, forMobile: false })} disabled={addBtnDisabled}>
                    Add New Popup Ad
                  </button>
                </p>
                <p className="ads__table-newad-subtitle fs-18">
                  Popup ads will show in supported clients
                </p>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      {!!adData && (
        <Modal handleClose={closeModal} className="ads__modalcontent">
          <p className="fs-24">Popup Ads Editor</p>
          <form onSubmit={submitForm}>
            <input type="hidden" name="isActive" value="true" />
            <div className="ads__modalcontent-top" style={{ width: "100%" }}>
              <div className="ads__modalcontent-top-inputs" style={{ width: "100%" }}>
                <InputField
                  label="Title"
                  className="mb-12"
                  id="title"
                  name="title"
                  value={adData.title}
                  onChange={onChange}
                />
                <InputField
                  label="Body"
                  className="mb-12"
                  id="body"
                  name="body"
                  multiline
                  rows={5}
                  value={adData.body}
                  onChange={onChange}
                />
                {!adData?.id ? (
                  <label className="ads__modalcontent-top-selectimage" htmlFor="image">
                    <p className="fs-20">Select Image</p>
                    <p className="fs-16">(.jpg, .png, .jpeg)</p>
                    <input type="file" id="image" name="image" onChange={onFileChange} />
                  </label>
                ) : (
                  <InputField
                    label="Image URL"
                    className="mb-12"
                    id="imageUrl"
                    name="imageUrl"
                    value={adData.imageUrl}
                    onChange={onChange}
                  />
                )}
                <InputField
                  label="Action URL"
                  className="mb-12"
                  id="actionUrl"
                  name="actionUrl"
                  value={adData.actionUrl}
                  onChange={onChange}
                />
                <div className="mb-12">
                  <CheckBox
                    text="For Mobile"
                    name="forMobile"
                    checked={!!adData.forMobile}
                    setChecked={onToggleForMobile}
                  />
                </div>
              </div>
            </div>

            <div className="ads__modalcontent-cta">
              <button
                className="btn primary light fullwidth"
                disabled={form.state.submitting || !adData.title || !adData.body || (!adData?.id && !adData?.image)}
              >
                {adData?.id ? "Update" : "Add"}
              </button>
            </div>
          </form>
        </Modal>
      )}
    </div>
  );
};

export default PopupAdsPage;
